import { z } from 'zod';

import type { NewRole, Role, RoleEdit } from '@/api/vacancies';

/**
 * **The four fields a vacancy is, as one schema** — §6.8's *New vacancy* dialog and its *Edit* write
 * the same vacancy, so they are described once (`.claude/rules/frontend.md` → Forms: the zod schema
 * **is** the form type), the way `personFields.ts` describes a person once for §6.4 and §6.5.
 *
 * ⚠️ **The bounds are `RoleIn`'s own, and they are here so the first refusal is the form and not a
 * 422.** `title ≤ 200`, `department ≤ 100`, `abbreviation ≤ 4`, all three non-empty — the numbers the
 * OpenAPI document declares for `RoleIn` (`maxLength` / `minLength`, from
 * `Role.title/department/abbreviation`'s `max_length`). They are **not** believed on trust:
 * `vacancyWrites.test.tsx` reads them out of `openapi.snapshot.json` and asserts this object equals
 * them, and asserts the schema's field names equal `RoleIn`'s own properties — so a bound the backend
 * changes, or a field it adds, fails the suite **with the snapshot refreshed**, instead of failing in
 * front of somebody typing. ⚠️ **That qualifier is the whole of it** (added 2026-08-09, BUG-070): the
 * assertion reads a committed file this repository owns, so it learns of a backend change at
 * regeneration and not before. It is an **equality**, so it does go red then — unlike an *absence*,
 * which by that moment has nothing left to announce (BUG-058 measured the difference).
 *
 * ⚠️ **The abbreviation input deliberately carries no `maxLength` attribute.** A native `maxLength`
 * would make the fifth character untypeable, which sounds like the same thing and is not: the refusal
 * would then be invisible (no message, nothing to correct) and — the reason it matters here — the
 * board's criterion *"a 5-character abbreviation is refused in the form and no request is sent"* would
 * be unfalsifiable, because nothing could put a fifth character in the field to be refused. The bound
 * is validated and **said**, below the field, as §4 requires.
 */
export const VACANCY_LIMITS = {
  title: 200,
  department: 100,
  abbreviation: 4,
} as const;

/**
 * Every value is trimmed **by the schema**, so the body sent and the words the person typing them can
 * see they typed are the same strings — and a title of spaces is empty rather than 3 characters long.
 */
export const vacancyFormSchema = z.object({
  title: z.string().trim().min(1).max(VACANCY_LIMITS.title),
  department: z.string().trim().min(1).max(VACANCY_LIMITS.department),
  abbreviation: z.string().trim().min(1).max(VACANCY_LIMITS.abbreviation),
  /**
   * §6.8 names the open flag in the create dialog (*"dialog: title, department, abbreviation, open
   * flag"*), and `createRole` states it rather than omitting it.
   *
   * ⚠️ **It is not rendered when the dialog is editing** (`VacancyDialog`), and that is a decision
   * about ADR 0005 rather than about layout: closing a vacancy is the one write with something to
   * explain, so it happens through the menu action that explains it (`CloseVacancyDialog`) and not
   * through a checkbox in a form whose button says *Save changes*. Held in the form regardless, from
   * the vacancy as it stands, so the diff below has something true to compare against and can never
   * send a value nobody chose.
   */
  is_open: z.boolean(),
});

export type VacancyForm = z.infer<typeof vacancyFormSchema>;

/** The vacancy as the form holds it — the defaults of an *Edit*. */
export const fieldsOf = (role: Role): VacancyForm => ({
  title: role.title,
  department: role.department,
  abbreviation: role.abbreviation,
  is_open: role.is_open,
});

/**
 * `RoleIn`'s **four fields and nothing else** — no `id`, no `tenant`, nothing the form happens to
 * carry. The tenant is resolved from the session on the backend (`.claude/rules/tenancy.md`), and
 * `vacancyWrites.test.tsx` asserts the recorded request body is exactly this object.
 */
export const creationOf = (submitted: VacancyForm): NewRole => ({
  title: submitted.title,
  department: submitted.department,
  abbreviation: submitted.abbreviation,
  is_open: submitted.is_open,
});

/**
 * **Only what changed, and never an explicit `null`.**
 *
 * `RoleUpdate` is *"every field optional, only the ones sent are written"*, and optional there means
 * **omittable, not nullable**: `{"title": null}` is a **422**, deliberately, so a `NULL` is never
 * written onto a NOT NULL column. Every value here comes from the form — three trimmed strings and a
 * boolean — so a `null` is not merely avoided but unproduceable.
 *
 * The four comparisons are written out rather than looped over the schema's keys because the fields
 * are not one type (`PERSON_FIELDS` loops because a person is four strings). The loop is replaced by
 * a test instead: `vacancyWrites.test.tsx` asserts that a submission differing in **every** field
 * produces a body whose keys are exactly `vacancyFormSchema`'s own — so a fifth field added above and
 * forgotten here fails the suite rather than being silently undiffable.
 */
export function changesOf(role: Role, submitted: VacancyForm): RoleEdit {
  const changes: RoleEdit = {};

  if (submitted.title !== role.title) changes.title = submitted.title;
  if (submitted.department !== role.department) changes.department = submitted.department;
  if (submitted.abbreviation !== role.abbreviation) changes.abbreviation = submitted.abbreviation;
  if (submitted.is_open !== role.is_open) changes.is_open = submitted.is_open;

  return changes;
}
